import { Attribute, GSQLUdtJson, supportedContainerValueTypes, supportedMapKeyTypes } from '@tigergraph/tools-models';
import { EditableInput } from '@/components/editableInput';
import { StyledSelect } from '@/components/schemaDesigner/StyledSelect';
import { OnChangeParams, Option } from 'baseui/select';
import { useQuery } from 'react-query';
import { axiosCluster } from '@/lib/network';
import { GLOBAL_VIEW, useUserContext } from '@/contexts/userContext';
import { useEffect, useState } from 'react';

export interface AttributeValueColumnProps {
  attribute: Attribute;
  onChange: (attr: Attribute) => void;
  readOnly?: boolean;
}

export function AttributeValueColumn({ attribute, onChange, readOnly }: AttributeValueColumnProps) {
  const { currentGraph } = useUserContext();
  const [udtOptions, setUdtOptions] = useState<Option[]>([]);
  const [udtValue, setUdtValue] = useState<Option[]>([]);
  const [shouldShowDefaultValue, setShouldShowDefaultValue] = useState(false);
  const [shouldShowValueType, setShouldShowValueType] = useState(false);
  const [shouldShowTupleName, setShouldShowTupleName] = useState(false);
  const [shouldShowValueTupleName, setShouldShowValueTupleName] = useState(false);

  const { data: udtList } = useQuery(['getUdtList', currentGraph], async () => {
    const url =
      currentGraph === GLOBAL_VIEW ? '/api/gsql-server/gsql/udt' : `/api/gsql-server/gsql/udt?graph=${currentGraph}`;
    const response = await axiosCluster.get(url);
    return response.data.results;
  });

  const hasVertexOrEdgeType = (udt: GSQLUdtJson) => {
    for (let i = 0; i < udt.fields.length; i++) {
      const type = udt.fields[i].fieldType;
      if (type === 'VERTEX' || type === 'EDGE') {
        return true;
      }
    }
    return false;
  };

  const hasVarLengthString = (udt: GSQLUdtJson) => {
    for (const field of udt.fields) {
      if (field.fieldType === 'STRING' && field.length === undefined) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    udtList &&
      setUdtOptions(
        udtList
          .filter((udt: GSQLUdtJson) => !hasVertexOrEdgeType(udt))
          .filter((udt: GSQLUdtJson) => !hasVarLengthString(udt))
          .map((udt: GSQLUdtJson) => ({ id: udt.name, label: udt.name }))
      );
  }, [udtList]);

  useEffect(() => {
    const showTupleName = ['UDT'];
    const showValueType = ['SET', 'LIST', 'MAP'];
    const showDefaultValue = ['INT', 'UINT', 'BOOL', 'FLOAT', 'DOUBLE', 'STRING', 'DATETIME'];
    setShouldShowDefaultValue(showDefaultValue.includes(attribute.type.name));
    setShouldShowValueType(showValueType.includes(attribute.type.name));
    setShouldShowTupleName(showTupleName.includes(attribute.type.name));
    setShouldShowValueTupleName(showTupleName.includes(attribute.type.valueTypeName));
  }, [attribute.type.name, attribute.type.valueTypeName]);

  const showKeyType = ['MAP'];

  const attrMapKeyOptions = supportedMapKeyTypes
    .filter((type) => type !== 'STRING COMPRESS')
    .map((type) => ({ id: type, label: type }));

  const attrContainerValueOptions = supportedContainerValueTypes
    .filter((type) => type !== 'STRING COMPRESS')
    .map((type) => ({ id: type, label: type }));

  const handleDefaultValueChange = (value: string) => {
    attribute.defaultValue = value;
    onChange(attribute);
  };

  const handleValueTypeChange = (params: OnChangeParams) => {
    attribute.type.valueTypeName = params.value[0].id as string;
    attribute.type.hasValueTypeName = true;
    attribute.type.hasValueTypeTupleName = params.value[0].id === 'UDT';
    onChange(attribute);
  };

  const handleKeyTypeChange = (params: OnChangeParams) => {
    attribute.type.keyTypeName = params.value[0].id as string;
    attribute.type.hasKeyTypeName = true;
    onChange(attribute);
  };

  const handleUdtNameChange = (params: OnChangeParams) => {
    const udtName = params.value[0].id as string;
    console.log(params, shouldShowTupleName, shouldShowValueTupleName);
    if (shouldShowTupleName) {
      attribute.type.hasTupleName = true;
      attribute.type.tupleName = udtName;
      setUdtValue(udtOptions.filter((type) => type.id === udtName));
    } else if (shouldShowValueTupleName) {
      attribute.type.hasValueTypeTupleName = true;
      attribute.type.valueTypeTupleName = udtName;
      setUdtValue(udtOptions.filter((type) => type.id === udtName));
    }
    onChange(attribute);
  };

  return (
    <>
      {attribute.isPrimaryKey ? (
        <></>
      ) : (
        <>
          {shouldShowDefaultValue && (
            <EditableInput value={attribute.defaultValue} readOnly={readOnly} onChange={handleDefaultValueChange} />
          )}
          {showKeyType.includes(attribute.type.name) && (
            <>
              <StyledSelect
                value={attrMapKeyOptions.filter((type) => type.id === attribute.type.keyTypeName)}
                options={attrMapKeyOptions}
                placeholder={'Key type'}
                onChange={handleKeyTypeChange}
              />
            </>
          )}
          {shouldShowValueType && (
            <StyledSelect
              value={attrContainerValueOptions.filter((type) => type.id === attribute.type.valueTypeName)}
              options={attrContainerValueOptions}
              placeholder={'Value type'}
              onChange={handleValueTypeChange}
            />
          )}
          {(shouldShowTupleName || shouldShowValueTupleName) && !shouldShowDefaultValue && (
            <StyledSelect
              noResultsMsg={'This graph has no UDT names'}
              value={udtValue}
              options={udtOptions}
              placeholder={'UDT name'}
              onChange={handleUdtNameChange}
            />
          )}
        </>
      )}
    </>
  );
}
